import { Component, forwardRef, Host, Input, OnInit, Optional, SkipSelf } from '@angular/core';
import {
  ControlContainer,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';

import { MaterialColor } from '@material/material-color.enum';
import { validUrl } from '@shared/utils';

import { SocialInfo } from '../models/socialinfo';

@Component({
  selector: 'mojo-social-info',
  templateUrl: './social-info.component.html',
  styleUrls: ['./social-info.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // tslint:disable-next-line: no-use-before-declare no-forward-ref
      useExisting: forwardRef(() => SocialInfoComponent),
      multi: true,
    },
  ],
})
export class SocialInfoComponent implements OnInit {
  @Input()
  controlName: string;

  @Input()
  socialInfo: SocialInfo[];

  @Input()
  color = MaterialColor.ACCENT;

  @Input() fromTalentPool = false;

  selected = [];
  validSocialInfo = [
    { domain: 'LinkedIn', url: 'linkedin.com/' },
    { domain: 'Facebook', url: 'facebook.com/' },
    { domain: 'Twitter', url: 'twitter.com/' },
    { domain: 'Instagram', url: 'instagram.com/' },
    { domain: 'GitHub', url: 'github.com/' },
    { domain: 'Stackoverflow', url: 'stackoverflow.com/' },
    { domain: 'Crunchbase', url: 'crunchbase.com/' },
    { domain: 'YouTube', url: 'youtube.com/' },
    { domain: 'Pinterest', url: 'pinterest.com/' },
  ];
  constructor(
    private fb: UntypedFormBuilder,
    @Optional()
    @Host()
    @SkipSelf()
    public controlContainer: ControlContainer,
  ) {}

  ngOnInit(): void {
    !!this.socialInfo && this.socialInfo.length > 0
      ? this.socialInfo.forEach((info, index) => this.pushSocialInfoGroup(index, info))
      : this.pushSocialInfoGroup(0);
  }

  pushSocialInfoGroup(index: number, socialInfo: Partial<SocialInfo> = {}) {
    let arrayValidators = [];

    if (this.fromTalentPool) {
      arrayValidators = [Validators.pattern(validUrl)];
    } else {
      arrayValidators = [
        Validators.pattern(validUrl),
        this.socialInfoUrlValidator(index).bind(this),
      ];
    }

    const controls = this.getParentControl();
    controls.push(
      this.fb.group({
        type: new UntypedFormControl(!!socialInfo.type ? socialInfo.type : ''),
        url: new UntypedFormControl(!!socialInfo.url ? socialInfo.url : '', arrayValidators),
        socialMediaType: new UntypedFormControl(),
      }),
    );
    this.selected.push(this.getSelectionUrl(socialInfo.url));
  }

  private getSelectionUrl(socialInfoUrl: string): string {
    const socialInfoObj = this.validSocialInfo
      .filter((obj) => !!obj.url && obj.url !== '')
      .find((obj) => socialInfoUrl?.includes(obj.url));
    return socialInfoObj ? socialInfoObj.url : '';
  }

  addSocialInfo(index: number) {
    this.pushSocialInfoGroup(index);
  }

  removeSocialInfo(index: number) {
    const control = this.getParentControl();
    control.removeAt(index);
    this.selected[index] = undefined;
  }

  getParentControl(): UntypedFormArray {
    return this.controlContainer.control.get(this.controlName) as UntypedFormArray;
  }

  socialInfoUrlValidator(index: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (this.selected[index] === '' && control.value !== '') {
        return { urlTypeMismatch: true };
      }
      return control.value.includes(this.selected[index]) ? null : { urlTypeMismatch: true };
    };
  }

  hasError(index: number): boolean {
    const control = this.getParentControl().controls[index].get('url');
    control.updateValueAndValidity();
    return (
      !!control &&
      control.invalid &&
      (control.dirty || control.touched) &&
      (this.isUrlMismatch(control) || this.isUrlInvalid(control))
    );
  }

  isUrlMismatch(control: AbstractControl) {
    return control.hasError('urlTypeMismatch');
  }

  isUrlInvalid(control: AbstractControl) {
    return control.hasError('pattern');
  }

  getErrorMsg(index: number) {
    const control = this.getParentControl().controls[index].get('url');
    if (this.isUrlInvalid(control)) {
      return 'URL is not valid';
    }
    if (this.isUrlMismatch(control)) {
      return 'URL must match social media type';
    }
  }
}
